import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const testimonials = [
  {
    name: "Jasmine T.",
    location: "Atlanta, GA",
    rating: 5,
    text: "I ordered The Empress package and I was literally screaming when I opened those golden eggs! The designer bag inside was beyond what I expected. Vicki Ann truly curates magic.",
    package: "The Empress",
  },
  {
    name: "Crystal M.",
    location: "Houston, TX",
    rating: 5,
    text: "This was a birthday gift for my sister and she cried happy tears! The jewelry and accessories were gorgeous. The whole unboxing experience felt so luxurious.",
    package: "The Countess",
  },
  {
    name: "Tasha W.",
    location: "Charlotte, NC",
    rating: 5,
    text: "I've ordered three times now and every single scoop has been incredible. The mystery aspect makes it so exciting — you never know what designer piece you're getting!",
    package: "The Duchess",
  },
  {
    name: "Monica R.",
    location: "Miami, FL",
    rating: 5,
    text: "The Queen's Crown package was worth every penny. I received a stunning designer-inspired purse, beautiful pearl accessories, and the presentation was absolutely breathtaking.",
    package: "The Queen's Crown",
  },
  {
    name: "Keisha D.",
    location: "Dallas, TX",
    rating: 5,
    text: "Watched the live reveal on TikTok and had to try it myself. The golden eggs, the pearls, the mystery capsules — everything about this experience is pure luxury!",
    package: "The Royal Highness",
  },
];

function StarRating({ rating }) {
  return (
    <div className="flex gap-1" data-testid="testimonial-stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={`star-${i}`}
          className={`w-4 h-4 ${
            i < rating
              ? "text-[#D4AF37] fill-[#D4AF37]"
              : "text-[#D4AF37]/20"
          }`}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(0);

  const next = () => {
    setDirection(1);
    setActive((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setDirection(-1);
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, []);

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
  };

  const t = testimonials[active];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 section-glow-gold" />

      {/* Decorative floating elements */}
      <div className="absolute top-20 left-10 w-48 h-48 rounded-full bg-[#D4AF37]/[0.02] blur-3xl" />
      <div className="absolute bottom-20 right-10 w-64 h-64 rounded-full bg-[#FF007F]/[0.02] blur-3xl" />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-great-vibes text-[#FF007F] text-2xl md:text-3xl mb-4">
            What Our Queens Say
          </p>
          <h2 className="font-playfair text-[#D4AF37] text-4xl md:text-5xl font-bold">
            Testimonials
          </h2>
          <div className="gold-line w-24 mx-auto mt-6" />
        </motion.div>

        {/* Testimonial Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <div className="glass-card rounded-2xl border border-[#D4AF37]/15 p-8 md:p-12 relative min-h-[280px] flex flex-col items-center justify-center">
            {/* Quote icon */}
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#0a0a0a] border border-[#D4AF37]/30 flex items-center justify-center">
              <Quote className="w-4 h-4 text-[#D4AF37]" />
            </div>

            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={active}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="flex flex-col items-center text-center"
              >
                <StarRating rating={t.rating} />

                <p
                  className="font-montserrat text-white/75 text-sm md:text-base leading-relaxed mt-6 mb-8 max-w-2xl italic"
                  data-testid="testimonial-text"
                >
                  "{t.text}"
                </p>

                <div>
                  <p
                    className="font-playfair text-white text-lg font-semibold"
                    data-testid="testimonial-name"
                  >
                    {t.name}
                  </p>
                  <p className="font-montserrat text-white/40 text-xs tracking-widest uppercase mt-1">
                    {t.location}
                  </p>
                  <p className="font-montserrat text-[#FF007F]/70 text-xs tracking-wider mt-2">
                    Ordered: {t.package}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Nav arrows */}
            <button
              onClick={prev}
              data-testid="testimonial-prev-btn"
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={next}
              data-testid="testimonial-next-btn"
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2.5 mt-8" data-testid="testimonial-dots">
            {testimonials.map((t, i) => (
              <button
                key={t.name}
                onClick={() => {
                  setDirection(i > active ? 1 : -1);
                  setActive(i);
                }}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === active
                    ? "w-8 bg-[#D4AF37]"
                    : "w-1.5 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/40"
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
